<div class="signup-container">
  <!-- Logo Section -->
  <div class="logo-section">
    <img class="logo" src="/assets/img/fleetoperate_logo.jpg" alt="FleetOperate Logo" />
  </div>

  <div class="signup-card">
    <!-- Header Section -->
    <div class="header-section">
      @if (!carrierCompanyName || isCancelled) {
        <h1 class="signup-title">
          Create Your Account
        </h1>
      } @else {
        <h1 class="signup-title">Join {{ carrierCompanyName }}</h1>
        <p class="signup-subtitle">Create your driver account to get started</p>
      }
    </div>

    <!-- Main Content -->
    <div class="signup-content">
      <!-- Error/Success Messages -->
      @if (errorMessage) {
        <mat-error class="error-message">
          <mat-icon>error</mat-icon>
          {{ errorMessage }}
        </mat-error>
      }

      @if (successMessage) {
        <div class="success-message" data-test="signup-successful-message">
          <mat-icon>check_circle</mat-icon>
          {{ successMessage }}
        </div>
      }

      <!-- Sign Up Form -->
      <form [formGroup]="form" #signupForm="ngForm" (ngSubmit)="onSubmit(signupForm)" class="signup-form">
        <div class="form-fields">
          <!-- Name Fields Row -->
          <div class="name-fields-row">
            <!-- First Name Field -->
            <mat-form-field appearance="outline" class="name-field">
              <mat-label>First Name</mat-label>
              <input
                matInput
                #firstName
                formControlName="firstName"
                maxlength="20"
                data-test="firstname-form-field"
                autocomplete="given-name"
                />
              <mat-icon matPrefix>person</mat-icon>
              @if (form.get('firstName')?.touched && form.get('firstName')?.invalid) {
                <mat-error>
                  {{ getFirstNameErrorMessage() }}
                </mat-error>
              }
            </mat-form-field>

            <!-- Last Name Field -->
            <mat-form-field appearance="outline" class="name-field">
              <mat-label>Last Name</mat-label>
              <input
                matInput
                formControlName="lastName"
                maxlength="20"
                data-test="lastname-form-field"
                autocomplete="family-name"
                />
              <mat-icon matPrefix>person_outline</mat-icon>
              @if (form.get('lastName')?.touched && form.get('lastName')?.invalid) {
                <mat-error>
                  {{ getLastNameErrorMessage() }}
                </mat-error>
              }
            </mat-form-field>
          </div>

          <!-- Email Field -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Email Address</mat-label>
            <input
              type="email"
              inputmode="email"
              matInput
              formControlName="email"
              data-test="email-form-field"
              [readonly]="driverEmail && !isCancelled"
              autocomplete="email"
              />
            <mat-icon matPrefix>email</mat-icon>
            @if (form.get('email')?.touched && form.get('email')?.invalid) {
              <mat-error>
                {{ getEmailErrorMessage() }}
              </mat-error>
            }
          </mat-form-field>

          <!-- Mobile Number Field -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Mobile Number</mat-label>
            <input
              matInput
              appPhoneMask
              maxlength="12"
              formControlName="mobileNumber"
              data-test="contact-number-form-field"
              autocomplete="tel"
              />
            <mat-icon matPrefix>phone</mat-icon>
            <mat-hint>Format: ###-###-####</mat-hint>
            @if (form.get('mobileNumber')?.touched && form.get('mobileNumber')?.invalid) {
              <mat-error>
                {{ getMobileNumberErrorMessage() }}
              </mat-error>
            }
          </mat-form-field>

          <!-- Password Field -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Password</mat-label>
            <input
              matInput
              [type]="hidePassword ? 'password' : 'text'"
              formControlName="password"
              data-test="password-form-field"
              autocomplete="new-password"
              />
            <mat-icon matPrefix>lock</mat-icon>
            <mat-icon
              matSuffix
              (click)="hidePassword = !hidePassword"
              class="password-toggle"
              >
              {{ hidePassword ? 'visibility' : 'visibility_off' }}
            </mat-icon>
          </mat-form-field>

          <!-- Password Requirements -->
          @if ((form.get('password')?.touched || form.get('password')?.dirty) && form.get('password')?.invalid) {
            <div class="password-requirements">
              <div class="requirements-header">
                <mat-icon class="requirements-icon">security</mat-icon>
                <span>Your password must contain at least:</span>
              </div>
              <div class="requirements-list">
                <div class="requirement-item"
                  [ngClass]="form.get('password')?.hasError('hasLength') || form.get('password')?.errors?.['required'] || loading ? 'requirement-error' : 'requirement-success'">
                  <mat-icon class="requirement-icon">{{
                    form.get('password')?.hasError('hasLength') || form.get('password')?.errors?.['required'] || loading ? 'close' : 'check'
                  }}</mat-icon>
                  <span>8 characters</span>
                </div>
                <div class="requirement-item"
                  [ngClass]="form.get('password')?.hasError('hasUpper') || form.get('password')?.errors?.['required'] || loading ? 'requirement-error' : 'requirement-success'">
                  <mat-icon class="requirement-icon">{{
                    form.get('password')?.hasError('hasUpper') || form.get('password')?.errors?.['required'] || loading ? 'close' : 'check'
                  }}</mat-icon>
                  <span>1 uppercase letter</span>
                </div>
                <div class="requirement-item"
                  [ngClass]="form.get('password')?.hasError('hasLower') || form.get('password')?.errors?.['required'] || loading ? 'requirement-error' : 'requirement-success'">
                  <mat-icon class="requirement-icon">{{
                    form.get('password')?.hasError('hasLower') || form.get('password')?.errors?.['required'] || loading ? 'close' : 'check'
                  }}</mat-icon>
                  <span>1 lowercase letter</span>
                </div>
                <div class="requirement-item"
                  [ngClass]="form.get('password')?.hasError('hasNumber') || form.get('password')?.errors?.['required'] || loading ? 'requirement-error' : 'requirement-success'">
                  <mat-icon class="requirement-icon">{{
                    form.get('password')?.hasError('hasNumber') || form.get('password')?.errors?.['required'] || loading ? 'close' : 'check'
                  }}</mat-icon>
                  <span>1 number</span>
                </div>
                <div class="requirement-item"
                  [ngClass]="form.get('password')?.hasError('hasSpecial') || form.get('password')?.errors?.['required'] || loading ? 'requirement-error' : 'requirement-success'">
                  <mat-icon class="requirement-icon">{{
                    form.get('password')?.hasError('hasSpecial') || form.get('password')?.errors?.['required'] || loading ? 'close' : 'check'
                  }}</mat-icon>
                  <span>1 special character (! @ # $ % ^ & *)</span>
                </div>
                @if (form.get('password')?.hasError('invalidCharacters')) {
                  <div class="requirement-item"
                    [ngClass]="form.get('password')?.hasError('invalidCharacters') ? 'requirement-error' : 'requirement-success'">
                    <mat-icon class="requirement-icon">close</mat-icon>
                    <span>Only these special characters are allowed: ! @ # $ % ^ & *</span>
                  </div>
                }
              </div>
            </div>
          }
        </div>

        <!-- Sign Up Button -->
        <button
          mat-raised-button
          color="primary"
          type="submit"
          class="signup-button"
          data-test="signup-button"
          [disabled]="loading || !form.valid"
          >
          @if (loading) {
            <mat-icon>hourglass_empty</mat-icon>
          }
          @if (!loading) {
            <span>Create Account</span>
          }
          @if (loading) {
            <span>Creating Account...</span>
          }
        </button>

        <!-- Terms and Conditions -->
        <div class="terms-text">
          By creating an account, you agree to our
          <a [routerLink]="[]" (click)="onNavigateToTermsConditions()" class="link">Terms and Conditions</a> and
          <a [routerLink]="[]" (click)="onNavigateToPrivacyStatement()" class="link">Privacy Statement</a>
        </div>
      </form>
    </div>

    <!-- Sign In Link -->
    <div class="signin-section">
      <p class="signin-text">
        Already have an account?
        <a [routerLink]="[]" (click)="onNavigateToSignIn()" class="signin-link">
          Sign in
        </a>
      </p>
    </div>

    <!-- reCAPTCHA Terms -->
    <div class="recaptcha-terms">
      This site is protected by reCAPTCHA and the Google
      <a href="https://policies.google.com/privacy" class="recaptcha-link">Privacy Policy</a> and
      <a href="https://policies.google.com/terms" class="recaptcha-link">Terms of Service</a> apply.
    </div>
  </div>
</div>
